import { AppConstants } from "@/config/AppConstants";
import { openWindow } from "@/functions/WindowHandler";
import { FEMediaUrl } from "@/models/frontend-only/FEMediaItem";
import { MediaPlayerStatus } from "@/models/frontend-only/MediaPlayerStatus";
import { BroadcastChannel } from "broadcast-channel";
import { defineStore } from "pinia";
import { RaygunFunctions } from "@/config/RayGun";
import pkceChallenge from "pkce-challenge";

declare let window: {
  onSpotifyWebPlaybackSDKReady: () => void;
  player: () => void;
} & Window;
declare let Spotify: any;

const clientId = AppConstants.SPOTIFY.CLIENT_ID;
const callbackUrl = window.location.origin + "/callback/spotify";
const scopes = "streaming user-read-private user-read-email";

export const useSpotifyStore = defineStore("spotify", () => {
  const toastStore = useToastStore();
  const { t } = useI18n();
  const fullPlaybackStore = useFullSongPlaybackStore();
  const debug = useDebug();

  const authorized = ref(false);
  let playState = false;
  const status = ref<MediaPlayerStatus>(MediaPlayerStatus.Unloaded);
  let error = "";
  let player: any = null;
  let playerState: any;
  let playerId = "";
  const playerReady = ref(false);
  let pollIntervalRef: number | null = null;
  const currentTime = ref(0);
  const duration = ref(0);
  let songId = "";

  const SpotifySettings = {
    expires: 0,
    refreshToken: "",
    accessToken: "",
  };

  async function getSettings() {
    debug.log("loading tokens from user meta - spotify");
    const settings = await getFullSongIntegrationTokens();
    if (
      settings.service === "spotify" &&
      settings.authInfo &&
      settings.authInfo.refreshToken &&
      settings.authInfo.expires
    ) {
      SpotifySettings.expires = settings.authInfo.expires;
      SpotifySettings.refreshToken = settings.authInfo.refreshToken;
      SpotifySettings.accessToken = settings.authInfo.accessToken || "";
    } else {
      clearTokensFromUserMeta(true);
    }
  }

  function saveSettings() {
    const service = SpotifySettings.accessToken ? "spotify" : null;
    fullPlaybackStore.saveSettings(
      {
        accessToken: SpotifySettings.accessToken,
        refreshToken: SpotifySettings.refreshToken,
        expires: SpotifySettings.expires,
      },
      service,
    );
  }

  let authState = "";
  let pkcePair: {
    code_verifier: string;
    code_challenge: string;
  } | null = null;

  let seekOnPlay: {
    time: number;
    songId: string;
  } | null = null;

  function accessTokenExpired() {
    const now = new Date().getTime();
    const expires = SpotifySettings.expires;
    return now > expires;
  }

  async function refreshAccessToken() {
    if (!SpotifySettings.expires || !SpotifySettings.refreshToken) {
      return;
    }

    if (!accessTokenExpired()) {
      return;
    }

    debug.log("refreshing spotify token");
    const params = {
      grant_type: "refresh_token",
      refresh_token: SpotifySettings.refreshToken,
      client_id: AppConstants.SPOTIFY.CLIENT_ID,
    };
    try {
      const response = await fetch("https://accounts.spotify.com/api/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(params),
      });
      if (!response.ok) {
        throw new Error("Failed to refresh token");
      }
      const data = await response.json();
      SpotifySettings.accessToken = data.access_token;
      SpotifySettings.refreshToken = data.refresh_token;
      setExpires(data.expires_in);
      saveSettings();
    } catch (err) {
      toastStore.errorToast(t("SV.MES_SpotifyConnectionError"));
      await unauthorize(false);
    }
  }

  async function init() {
    if (
      import.meta.env.VITE_ENABLE_SPOTIFY !== "1" &&
      import.meta.env.VITE_ENABLE_SPOTIFY !== "new"
    ) {
      return;
    }
    debug.log("spotify init");
    status.value = MediaPlayerStatus.Loading;
    await getSettings();
    await refreshAccessToken();
    authorized.value = !!SpotifySettings.accessToken;
    if (authorized.value) {
      await connectToSpotify();
    }
    debug.log("spotify init complete");
  }

  async function authorize(isMobile = false) {
    authState = Math.random().toString(36).substring(7);
    pkcePair = await pkceChallenge(128);

    const params = {
      client_id: clientId,
      response_type: "code",
      redirect_uri: callbackUrl,
      code_challenge_method: "S256",
      code_challenge: pkcePair.code_challenge,
      scope: scopes,
      state: authState,
    };
    const url = "https://accounts.spotify.com/authorize";

    openedWindow.value = openWindow(url, params, isMobile);

    if (!openedWindow.value) {
      toastStore.errorToast(t("SV.MES_ErrorOpeningNewWindow"));
      throw new Error("Error creating window");
    }

    broadcastChannel.addEventListener("message", authorizeCallback);

    const timerRef = setInterval(() => {
      if (openedWindow.value && openedWindow.value.closed) {
        clearInterval(timerRef);
        openedWindow.value = null;
        broadcastChannel.removeEventListener("message", authorizeCallback);
      }
    }, 100);
  }

  const openedWindow = ref<Window | null>(null);

  const connecting = computed(() => !!openedWindow.value);
  const broadcastChannel = new BroadcastChannel("spotify-auth-callback");

  function onError(msg: string) {
    error = msg;
    status.value = MediaPlayerStatus.Error;
    debug.log(msg);
  }

  function toastErrorAuth() {
    toastStore.errorToast(t("SV.MES_SpotifyConnectionError"));
  }

  function setExpires(expiresIn: number) {
    // typically 3600 seconds or 1 hr
    const now = new Date();
    SpotifySettings.expires = now.getTime() + expiresIn * 1000;
  }

  async function authorizeCallback(event: {
    state: string;
    error: string;
    code: string;
  }) {
    broadcastChannel.removeEventListener("message", authorizeCallback);

    if (openedWindow.value) {
      debug.log("attempting to close window");
      openedWindow.value.close();
      openedWindow.value = null;
    }

    if (event.state !== authState) {
      toastErrorAuth();
      throw new Error("Spotify Auth State values do not match");
    }

    if (event.error) {
      toastErrorAuth();
      throw event.error;
    }

    if (!event.code) {
      toastErrorAuth();
      throw new Error("Missing spotify Auth Code");
    }

    if (!pkcePair) {
      toastErrorAuth();
      throw new Error("PKCEPair value missing");
    }

    const params = {
      client_id: AppConstants.SPOTIFY.CLIENT_ID,
      grant_type: "authorization_code",
      code: event.code,
      redirect_uri: callbackUrl,
      code_verifier: pkcePair.code_verifier,
    };

    let data: any;
    try {
      const response = await fetch("https://accounts.spotify.com/api/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(params),
      });
      data = await response.json();
    } catch (err) {
      toastStore.errorToast(t("SV.MES_SpotifyConnectionError"));
      throw err;
    }

    SpotifySettings.accessToken = data.access_token;
    SpotifySettings.refreshToken = data.refresh_token;

    setExpires(data.expires_in);

    authorized.value = true;
    await nextTick();
    saveSettings();
    debug.log("spotify auth access granted");

    if (!(await verifyUserHasPremium())) {
      return;
    }

    toastStore.successToast(t("SV.LIT_ConnectedToSpotify"));
    connectToSpotify();
  }

  function pollForStatus() {
    if (!pollIntervalRef) {
      debug.log("poll for status");
      pollIntervalRef = window.setInterval(checkStatus, 200);
    }
  }

  function unpollForStatus() {
    if (pollIntervalRef) {
      debug.log("unpoll for status");
      clearInterval(pollIntervalRef);
      pollIntervalRef = null;
    }
    checkStatus();
  }

  async function checkStatus() {
    if (!player || !playerReady.value) {
      status.value = MediaPlayerStatus.Loading;
      return;
    }

    playerState = await player.getCurrentState();

    debug.log("SPOTIFY: playerState", playerState);

    if (!playerState || playerState.loading) {
      status.value = MediaPlayerStatus.Loading;
      return;
    }

    if (
      status.value === MediaPlayerStatus.Playing &&
      currentTime.value !== 0 &&
      playerState.paused &&
      playerState.position === 0
    ) {
      //$emit('ended');
    }

    if (!playerState.paused) {
      status.value = MediaPlayerStatus.Playing;
      if (!seekOnPlay) {
        // debug.log("currentTime: checkStatus ", playerState.position / 1000);
        currentTime.value = playerState.position / 1000;
      }
    } else {
      currentTime.value = playerState.position / 1000;
      //detecting stopped point
      if (
        status.value === MediaPlayerStatus.Playing &&
        currentTime.value === 0
      ) {
        status.value = MediaPlayerStatus.Paused;
        playState = false;
      }
    }

    duration.value = playerState.duration / 1000;

    checkSeekTime();
  }

  async function checkSeekTime() {
    if (!seekOnPlay) {
      return;
    }

    if (seekOnPlay.songId !== songId || error || seekOnPlay.time === 0) {
      seekOnPlay = null;
      return;
    }

    if (
      !playState ||
      status.value !== MediaPlayerStatus.Playing ||
      playerState.disallows.seeking
    ) {
      return;
    }

    playerState = await player.getCurrentState();
    await player.setVolume(1);
    await seek(seekOnPlay.time);
    seekOnPlay = null;
    player.resume();
  }

  function seek(time: number) {
    debug.log("currentTime: seek ", time);
    currentTime.value = time;
    const minTime = Math.max(time * 1000, 1);
    // set to min of 1 to avoid error in seeking 0.
    return player.seek(minTime);
  }

  async function getSongFromISRC(isrc: string) {
    const profile = useProfileStore();
    const api = useApiStore();

    await waitUntilTrue(() => profile.profile);

    if (!profile.profile) {
      throw new Error("Profile not found");
    }

    const cacheKey = `spotify-search:${isrc}`;
    const countryCode = profile.profile.countryCode;
    //check to see if this isrc exists on songselect first
    const cachedItem = await api
      .post(
        `/GetTerritoryCachedItem?key=${cacheKey}&countryCode=${countryCode}`,
      )
      .catch((err) => {
        RaygunFunctions.sendSilentError("Failed to get cache item", {
          key: cacheKey,
          countryCode: countryCode,
          err,
        });
      });

    const debug = useDebug();
    debug.log("getting cached item", cacheKey, countryCode, cachedItem);

    if (cachedItem) {
      const jsonParsed = JSON.parse(cachedItem);
      return jsonParsed as {
        id: string;
        url: string;
        source: string;
        artwork: string;
        title: string;
        subtitle: string;
      };
    }

    let items: SpotifyTrack[] = [];
    try {
      items =
        (
          (await request({
            method: "GET",
            path: `/search`,
            query: {
              q: `isrc:${isrc}`,
              type: "track",
              market: profile.profile?.countryCode || "US",
              limit: "1",
              include_external: "audio",
            },
          })) as SpotifySearchResults
        )?.tracks.items || [];
    } catch (err) {
      debug.log("error getting song from isrc", err);
    }
    const track = items[0];
    if (!track) {
      return null;
    }

    const data = {
      id: track.id,
      url: track.album.external_urls.spotify,
      source: "Spotify",
      artwork: track.album.images[0]?.url || "",
      title: track.album.name,
      subtitle: track.artists.map((a) => a.name).join(", "),
    } as FEMediaUrl;

    api
      .post(
        `/SetTerritoryCachedItem?key=${cacheKey}&countryCode=${countryCode}`,
        JSON.stringify(data),
      )
      .catch((err) =>
        RaygunFunctions.sendSilentError("Failed to set cache item", {
          key: cacheKey,
          countryCode: countryCode,
          data,
          err,
        }),
      );
    return data;
  }

  async function connectToSpotify() {
    if (!(await verifyUserHasPremium())) {
      return;
    }

    const el = document.querySelector("#spotify-musicsdk-script");
    if (!el) {
      await new Promise<void>((resolve) => {
        window.onSpotifyWebPlaybackSDKReady = () => {
          resolve();
        };
        const script = document.createElement("script");
        script.id = "spotify-musicsdk-script";
        script.setAttribute("src", "https://sdk.scdn.co/spotify-player.js");
        document.body.appendChild(script);
        debug.log("appended script to body");
      });
      debug.log("loaded spotify to body");
    }

    player = new Spotify.Player({
      name: "SongSelect Spotify Player",
      getOAuthToken: (cb: (token: string) => void) => {
        cb(SpotifySettings.accessToken);
      },
    });

    // Error handling
    player.addListener("initialization_error", (data: { message: string }) => {
      debug.log("SPOTIFY: initialization error", data);
      onError(data.message);
      status.value === MediaPlayerStatus.Paused;
      playState = false;
    });

    player.addListener("authentication_error", (data: { message: string }) => {
      debug.log("SPOTIFY: authentication error", data);
      onError(data.message);
      status.value === MediaPlayerStatus.Paused;
      playState = false;
    });

    player.addListener("account_error", (data: { message: string }) => {
      debug.log("SPOTIFY: account error", data);
      onError(data.message);
      status.value === MediaPlayerStatus.Paused;
      playState = false;
    });

    player.addListener("playback_error", (data: { message: string }) => {
      debug.log("SPOTIFY: playback error", data);
      onError(data.message);
      status.value === MediaPlayerStatus.Paused;
      playState = false;
    });

    player.addListener(
      "player_state_changed",
      async (data: { message: string }) => {
        debug.log("SPOTIFY: player_state_changed", data);
        const state = await player.getCurrentState();
        if (!state.paused) {
          pollForStatus();
        } else {
          unpollForStatus();
        }
      },
    );

    // Ready
    player.addListener("ready", async (data: { device_id: string }) => {
      debug.log("SPOTIFY: ready", data);
      playerReady.value = true;
      playerId = data.device_id;
      window.player = player;
      //bus.$emit('spotifyReady');
      debug.log("SPOTIFY: SPOTIFY Ready with Device ID", data.device_id);
    });

    // Not Ready
    player.addListener("not_ready", (data: { device_id: string }) => {
      debug.log("SPOTIFY: not_ready", data);
      unpollForStatus();
      playerReady.value = false;
      playerId = data.device_id;
      debug.log("SPOTIFY: SPOTIFY Device ID has gone offline", data.device_id);
    });

    // Connect to the player!
    player.connect();
  }

  async function request(data: {
    path: string;
    method: "GET" | "POST" | "PUT" | "DELETE";
    data?: any;
    params?: any;
    query?: any;
    headers?: any;
  }) {
    await refreshAccessToken();

    if (!SpotifySettings.accessToken) {
      return Promise.reject({ status: 401 });
    }

    // set up search query
    let url = "https://api.spotify.com/v1" + data.path;
    if (data.query) {
      const searchQuery = new URLSearchParams(data.query || {}).toString();
      url += "?" + searchQuery;
    }

    // set up headers
    const requestHeaders: any = {};

    if (data.headers) {
      Object.assign(requestHeaders, data.headers);
    }

    requestHeaders["Authorization"] = `Bearer ${SpotifySettings.accessToken}`;

    // setup body
    let body = "{}";
    if (data.method !== "GET" && data.data) {
      body = JSON.stringify(data.data);
    }

    const response = await fetch(url, {
      method: data.method,
      headers: {
        ...requestHeaders,
        "Content-Type": "application/json",
      },
      ...((data.method !== "GET" && {
        body: body,
      }) ||
        {}),
    });

    if (!response.ok && response.status === 401) {
      toastStore.errorToast(t("SV.MES_SpotifyConnectionError"));
      await unauthorize(false);
      return null;
    }

    try {
      const responseData = await response.json();
      debug.log("Spotify Response", responseData);
      return responseData;
    } catch (err) {
      console.warn("spotify json response failed", response);
    }
  }

  async function clearTokensFromUserMeta(skipSaving = false) {
    SpotifySettings.expires = 0;
    SpotifySettings.refreshToken = "";
    SpotifySettings.accessToken = "";
    if (!skipSaving) {
      saveSettings();
    }
  }

  function playSong(id: string, atTime?: number | null) {
    debug.log("playSong", id);
    status.value === MediaPlayerStatus.Loading;
    if (atTime) {
      seekOnPlay = {
        time: atTime,
        songId: id,
      };
    }

    songId = id;
    playState = true;
    debug.log("currentTime: play song ", atTime);
    currentTime.value = atTime || 0;

    debug.log("PLAY SPOTIFY");
    if (atTime) {
      player.setVolume(0);
    }

    debug.log("play Song", id, playerId);
    return request({
      method: "PUT",
      path: `/me/player/play?device_id=${playerId}`,
      data: { uris: [`spotify:track:${id}`] },
    }).catch(() => {});
  }

  async function play() {
    debug.log("spotify play");
    if (!player || !playerReady.value || !authorized.value) {
      debug.log("spotify player not ready");
      playState = false;
      status.value === MediaPlayerStatus.Loading;
      return;
    }

    if (!playState) {
      if (!playerState?.track_window?.current_track?.id) {
        await playSong(songId);
      } else {
        playState = true;
        player.resume();
        status.value === MediaPlayerStatus.Loading;
      }
    } else {
      debug.log("spotify: not in play state, no play action");
    }
  }

  function pause() {
    playState = false;
    status.value = MediaPlayerStatus.Paused;
    if (
      !player ||
      !playerReady.value ||
      !authorized.value ||
      !playerState?.track_window?.current_track?.id
    ) {
      return;
    }
    player.pause();
  }

  function unauthorize(showMsg = true) {
    authState = "";
    pkcePair = null;
    pause();
    authorized.value = false;

    player = null;
    playerReady.value = false;
    duration.value = 0;
    clearTokensFromUserMeta();
    if (showMsg) {
      toastStore.successToast(t("SV.LIT_SpotifyDisconnected"));
    }
  }

  async function verifyUserHasPremium() {
    const spotifyUserData = await request({
      method: "GET",
      path: "/me",
    });
    if (!spotifyUserData || !spotifyUserData.product) {
      return false;
    }

    if (spotifyUserData.product !== "premium") {
      unauthorize(false);
      toastStore.errorToast(t("SV.MES_SpotifyPremiumError"));
      return false;
    }
    return true;
  }

  function seekTo(time: number) {
    player.seek(time * 1000);
  }
  init();

  return {
    authorize,
    authorizeCallback,
    unauthorize,
    play,
    pause,
    seekTo,
    playSong,
    connecting,
    openedWindow,
    authorized,
    currentTime,
    duration,
    status,
    getSongFromISRC,
    playerReady,
  };
});
